import { useEffect, useRef, useState } from "react";
import { MapPin, ExternalLink, Compass, AlertCircle, Home } from "lucide-react";
import { loadGoogleMaps, getGoogleMapsKey } from "@/lib/googleMaps";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Mapbox forward-geocode → { lat, lng } | null.
// Deliberately used instead of google.maps.Geocoder because Google's client
// geocoder requires billing enabled — but Street View rendering itself does
// not. This keeps address lookup free (Mapbox already powers our satellite
// view and measurements) while Google is used *only* for the Street View
// canvas the user sees on the customer profile.
async function mapboxGeocode(address) {
  if (!MAPBOX_TOKEN || !address) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&limit=1`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const j = await r.json();
    const feat = j.features && j.features[0];
    if (!feat || !feat.center) return null;
    const [lng, lat] = feat.center;
    return { lat, lng };
  } catch {
    return null;
  }
}

/**
 * StreetViewPanel
 * ---------------
 * Interactive Google Street View panorama for a saved property/service
 * address. Uses the Google Maps JavaScript API (`StreetViewService` +
 * `StreetViewPanorama`) so we can:
 *   - Geocode the address once.
 *   - Probe `getPanorama` within a 50 m radius before rendering.
 *   - Show a graceful fallback when coverage is missing.
 *
 * The map key is read from `REACT_APP_GOOGLE_MAPS_KEY` (env-only). Swapping
 * a demo key for a production key requires no code change — the watermark
 * and unbilled popup disappear automatically. Mapbox continues to handle
 * satellite tiles, drawing and measurements — Google Maps here is used
 * only for the Street View reference.
 */
export default function StreetViewPanel({ address, coord: coordProp = null, title = "Street View", className = "" }) {
  const panoRef = useRef(null);
  const panoInstance = useRef(null);
  // "armed" gates every Google-Maps side-effect: the JS API loader, the
  // Mapbox geocode fallback, the StreetViewService probe, and the panorama
  // mount. On first render (and whenever the address changes) it is `false`,
  // so we render a lightweight placeholder with a "Load Street View" button
  // and make **zero** Google Maps requests. Only after the user clicks that
  // button does the standard loader path run. This is the cost-control
  // behaviour requested in Iter39.
  const [armed, setArmed] = useState(false);
  const [status, setStatus] = useState("idle");
  // status ∈ "idle" | "loading" | "ok" | "no-coverage" | "no-key" | "auth-error" | "no-address" | "geocode-failed"
  const [coord, setCoord] = useState(coordProp); // { lat, lng } — used by "Open in Google Maps" (available without loading the panorama)

  // If the address (or caller-supplied coord) changes, drop back to idle so
  // the user has to explicitly re-arm the panorama for the new location.
  // Keeps stale panoramas from lingering on a different property.
  useEffect(() => {
    setArmed(false);
    setStatus("idle");
    // Preserve any caller-supplied coord so "Open in Google Maps" keeps
    // working without loading the panorama.
    setCoord(coordProp);
    if (panoRef.current) panoRef.current.innerHTML = "";
    panoInstance.current = null;
  }, [address, coordProp?.lat, coordProp?.lng]);

  useEffect(() => {
    if (!armed) return; // Iter39: no Google Maps traffic until the user clicks Load
    let cancelled = false;
    async function run() {
      if (!address) { setStatus("no-address"); return; }
      if (!getGoogleMapsKey()) { setStatus("no-key"); return; }
      setStatus("loading");
      try {
        // 1) Prefer a caller-supplied coord (already saved on the record from
        //    the address-autocomplete pick). This avoids a redundant Mapbox
        //    geocode round-trip and keeps Street View + Mapbox measurements
        //    pointed at the same coordinate.
        let latLng = coordProp;
        if (!latLng) {
          latLng = await mapboxGeocode(address);
        }
        if (cancelled) return;
        if (!latLng) { setStatus("geocode-failed"); return; }
        setCoord(latLng);
        // 2) Load Google Maps JS API and probe for Street View coverage.
        const g = await loadGoogleMaps();
        if (cancelled) return;
        const svc = new g.maps.StreetViewService();
        const panoData = await new Promise((resolve) => {
          svc.getPanorama(
            { location: latLng, radius: 50, source: g.maps.StreetViewSource.OUTDOOR },
            (data, s) => resolve({ data, s: String(s) }),
          );
        });
        if (cancelled) return;
        if (panoData.s !== "OK" || !panoData.data) {
          setStatus("no-coverage");
          return;
        }
        // 3) Mount the interactive panorama
        if (!panoRef.current) { setStatus("no-coverage"); return; }
        // Fresh panorama each address change — dispose the previous one
        // by clearing the container.
        panoRef.current.innerHTML = "";
        panoInstance.current = new g.maps.StreetViewPanorama(panoRef.current, {
          pano: panoData.data.location.pano,
          pov: { heading: 0, pitch: 0 },
          zoom: 1,
          addressControl: false,
          fullscreenControl: true,
          motionTracking: false,
          motionTrackingControl: false,
          linksControl: true,
          panControl: true,
          zoomControl: true,
          enableCloseButton: false,
        });
        setStatus("ok");
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || "";
        if (msg === "AUTH_FAILURE" || msg === "SCRIPT_LOAD_ERROR") setStatus("auth-error");
        else setStatus("no-coverage");
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [armed, address, coordProp?.lat, coordProp?.lng]);

  const openInGoogleMaps = () => {
    if (coord) {
      // Prefer a direct pano/streetview link on coord — falls back to search
      // if the pano can't be resolved externally.
      const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coord.lat},${coord.lng}`;
      window.open(url, "_blank", "noopener");
    } else if (address) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <div
      className={`bg-white border border-zinc-200 rounded-xl overflow-hidden card-crisp ${className}`}
      data-testid="street-view-panel"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
        <Compass className="w-4 h-4 text-[#FF5A00]" />
        <div className="font-heading font-bold text-sm">{title}</div>
        {address && (
          <span className="ml-auto text-[10px] text-zinc-500 font-mono-accent truncate max-w-[60%]" title={address}>
            {address}
          </span>
        )}
      </div>

      {/* Canvas — 240px on mobile, 320-360 on larger screens */}
      <div
        ref={panoRef}
        className={`w-full h-60 md:h-80 lg:h-72 xl:h-80 bg-zinc-100 ${status === "ok" ? "" : "hidden"}`}
        data-testid="street-view-canvas"
      />

      {status !== "ok" && (
        <div className="p-6 min-h-[240px] flex flex-col items-center justify-center text-center gap-3" data-testid="street-view-fallback">
          {status === "idle" && (
            <>
              <Home className="w-8 h-8 text-[#FF5A00]" />
              <div className="text-sm text-zinc-700 max-w-[26ch]">
                Street View is ready but not loaded yet. Click below to fetch the panorama for this property.
              </div>
              <button
                type="button"
                onClick={() => setArmed(true)}
                disabled={!address}
                className="inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-sm font-medium"
                data-testid="street-view-load-btn"
              >
                <Home className="w-4 h-4" /> Load Street View
              </button>
              {!address && (
                <div className="text-[11px] text-zinc-500">Add a service address to enable Street View.</div>
              )}
            </>
          )}
          {status === "loading" && (
            <div className="text-sm text-zinc-500">Loading Street View…</div>
          )}
          {status === "no-address" && (
            <>
              <MapPin className="w-8 h-8 text-zinc-300" />
              <div className="text-sm text-zinc-500">
                No address on file yet. Add a service address on this customer to see Street View here.
              </div>
            </>
          )}
          {status === "no-coverage" && (
            <>
              <AlertCircle className="w-8 h-8 text-zinc-400" />
              <div className="text-sm text-zinc-700">Google Street View isn't available at this address.</div>
              <div className="text-xs text-zinc-500">Try opening the location in Google Maps for a wider search.</div>
            </>
          )}
          {status === "geocode-failed" && (
            <>
              <AlertCircle className="w-8 h-8 text-zinc-400" />
              <div className="text-sm text-zinc-700">We couldn't locate this address on Google's map.</div>
              <div className="text-xs text-zinc-500">Double-check the address on the customer record.</div>
            </>
          )}
          {status === "no-key" && (
            <>
              <AlertCircle className="w-8 h-8 text-amber-500" />
              <div className="text-sm text-zinc-700">Street View isn't configured yet.</div>
              <div className="text-xs text-zinc-500">Add <code className="font-mono">REACT_APP_GOOGLE_MAPS_KEY</code> to the frontend environment.</div>
            </>
          )}
          {status === "auth-error" && (
            <>
              <AlertCircle className="w-8 h-8 text-rose-500" />
              <div className="text-sm text-zinc-700">Google rejected the Street View request.</div>
              <div className="text-xs text-zinc-500">Verify the key's referrer restrictions and that the Maps JavaScript API is enabled.</div>
            </>
          )}
        </div>
      )}

      <div className="px-4 py-3 border-t border-zinc-200 flex items-center gap-2">
        <button
          type="button"
          onClick={openInGoogleMaps}
          disabled={!address}
          className="inline-flex items-center gap-1.5 text-xs text-[#FF5A00] hover:text-[#E04F00] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="street-view-open-gmaps-btn"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open in Google Maps
        </button>
        {status === "ok" && (
          <span className="ml-auto text-[10px] text-emerald-700 uppercase tracking-widest font-mono-accent">Live coverage</span>
        )}
      </div>
    </div>
  );
}
